import { BrowserRouter, Routes, Route, Navigate, useSearchParams } from 'react-router-dom'
import React, { useEffect, lazy, Suspense, useMemo } from 'react'
import { useAuth, AuthProvider } from './contexts/AuthContext'
import { ThemeProvider } from './contexts/ThemeProvider'
import { initGlobalLogging } from './utils/logger'

// Layouts (Lazy)
const AdminLayout = lazy(() => import('./layouts/AdminLayout'))
const SuperAdminLayout = lazy(() => import('./layouts/SuperAdminLayout'))
const SecurityShield = lazy(() => import('./components/SecurityShield'))

// Pages (Lazy)
const LoginPage = lazy(() => import('./pages/auth/LoginPage'))
const DashboardPage = lazy(() => import('./pages/admin/DashboardPage'))
const PropertiesPage = lazy(() => import('./pages/admin/PropertiesPage'))
const LeadsPage = lazy(() => import('./pages/admin/LeadsPage'))
const SalesPage = lazy(() => import('./pages/admin/SalesPage'))
const AgentsPage = lazy(() => import('./pages/admin/AgentsPage'))
const ValuationsPage = lazy(() => import('./pages/admin/ValuationsPage'))
const ThemeConfigPage = lazy(() => import('./pages/admin/ThemeConfigPage'))
const WebConfigPage = lazy(() => import('./pages/admin/WebConfigPage'))
const BackupsPage = lazy(() => import('./pages/admin/BackupsPage'))
const LanguagesPage = lazy(() => import('./pages/admin/LanguagesPage'))
const TrackingPage = lazy(() => import('./pages/admin/TrackingPage'))
const PropertyTypesPage = lazy(() => import('./pages/admin/PropertyTypesPage'))
const ZonesPage = lazy(() => import('./pages/admin/ZonesPage'))
const ConservationStatesPage = lazy(() => import('./pages/admin/ConservationStatesPage'))
const LeadPipelinesPage = lazy(() => import('./pages/admin/LeadPipelinesPage'))
const ClientsPage = lazy(() => import('./pages/admin/ClientsPage'))
const AgendaPage = lazy(() => import('./pages/admin/AgendaPage'))
const DocumentsPage = lazy(() => import('./pages/admin/DocumentsPage'))
const DocumentCategoriesPage = lazy(() => import('./pages/admin/DocumentCategoriesPage'))
const BlogPage = lazy(() => import('./pages/admin/BlogPage'))
const BlogEditorPage = lazy(() => import('./pages/admin/BlogEditorPage'))
const UserManagementPage = lazy(() => import('./pages/admin/UserManagementPage'))
const PortalsPage = lazy(() => import('./pages/admin/PortalsPage'))
const PortalTestPage = lazy(() => import('./pages/admin/PortalTestPage'))
const ImportPropertiesPage = lazy(() => import('./pages/admin/ImportPropertiesPage'))
const PageBuilderPage = lazy(() => import('./pages/admin/PageBuilderPage'))
const MediaLibraryPage = lazy(() => import('./pages/admin/MediaLibraryPage'))
const SuperAdminPage = lazy(() => import('./pages/superadmin/SuperAdminPage'))
const SuperAdminLoginPage = lazy(() => import('./pages/superadmin/SuperAdminLoginPage'))
const SystemSchemaPage = lazy(() => import('./pages/admin/SystemSchemaPage'))
const MarketingDashboard = lazy(() => import('./pages/admin/marketing/MarketingDashboard'))
const TemplatesPage = lazy(() => import('./pages/admin/marketing/TemplatesPage'))
const TemplateEditor = lazy(() => import('./pages/admin/marketing/TemplateEditor'))
const CampaignHistoryPage = lazy(() => import('./pages/admin/marketing/CampaignHistoryPage'))
const SentEmailsPage = lazy(() => import('./pages/admin/SentEmailsPage'))
const UnderConstruction = lazy(() => import('./pages/admin/UnderConstruction'))

// PublicHome is kept as a direct import to ensure the landing page is as fast as possible
import PublicHome from './pages/public/PublicHome'

// Inicializar capturador de errores global
initGlobalLogging()

const PageLoading = () => (
  <div style={{ height: '80vh', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 20 }}>
    <div className="spinner" style={{ width: 40, height: 40, border: '3px solid #f3f3f3', borderTop: '3px solid var(--color-accent)', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
    <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
  </div>
)

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth()
  const location = window.location.pathname
  
  if (isLoading) return <PageLoading />
  
  if (!user) {
    const isSuperAdminPath = location.startsWith('/superadmin')
    return <Navigate to={(isSuperAdminPath ? "/superadmin/login" : "/login") + window.location.search} replace />
  }
  
  return <>{children}</>
}

function RoleGuard({ allowedRoles, children }: { allowedRoles: string[], children: React.ReactNode }) {
  const { profile } = useAuth()
  if (!profile || !profile.role) return <PageLoading />
  if (!allowedRoles.includes(profile.role)) return <Navigate to={"/admin/dashboard" + window.location.search} replace />
  return <>{children}</>
}

function AppRoutes() {
  const { user } = useAuth()
  return (
    <Suspense fallback={<PageLoading />}>
      <Routes>
        {/* Pàgina pública */}
        <Route path="/" element={<Navigate to={`/es${window.location.search}`} replace />} />
        <Route path="/:lang" element={<PublicHome />} />
        {/* URLs SEO-friendly */}
        <Route path="/:lang/:view/:slug" element={<PublicHome />} />
        <Route path="/:lang/:view" element={<PublicHome />} />
        <Route path="/p/:slug" element={<PublicHome />} />
        <Route path="/:lang/p/:slug" element={<PublicHome />} />


        {/* Login */}
        <Route path="/login" element={user ? <Navigate to={"/admin" + window.location.search} replace /> : <LoginPage />} />

        {/* Admin */}
        <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
          <Route index element={<Navigate to={"/admin/dashboard" + window.location.search} replace />} />
          <Route path="dashboard" element={<DashboardPage />} />
          <Route path="properties" element={<PropertiesPage />} />
          <Route path="properties/import" element={<ImportPropertiesPage />} />
          <Route path="tracking" element={<TrackingPage />} />
          <Route path="leads" element={<LeadsPage />} />
          <Route path="sales" element={<SalesPage />} />
          <Route path="contacts" element={<LeadsPage />} />
          <Route path="valuations" element={<ValuationsPage />} />
          <Route path="agents" element={<AgentsPage />} />
          <Route path="theme" element={<ThemeConfigPage />} />
          <Route path="webconfig" element={<WebConfigPage />} />
          <Route path="backups" element={<BackupsPage />} />
          <Route path="languages" element={<LanguagesPage />} />
          <Route path="property-types" element={<PropertyTypesPage />} />
          <Route path="zones" element={<ZonesPage />} />
          <Route path="conservation" element={<ConservationStatesPage />} />
          <Route path="lead-pipelines" element={<LeadPipelinesPage />} />
          <Route path="clients" element={<ClientsPage />} />
          <Route path="calendar" element={<AgendaPage />} />
          <Route path="docs" element={<DocumentsPage />} />
          <Route path="docs/categories" element={<DocumentCategoriesPage />} />

          <Route path="blog" element={<RoleGuard allowedRoles={['admin', 'owner', 'proprietari', 'superadmin']}><BlogPage /></RoleGuard>} />
          <Route path="blog/new" element={<RoleGuard allowedRoles={['admin', 'owner', 'proprietari', 'superadmin']}><BlogEditorPage /></RoleGuard>} />
          <Route path="blog/edit/:id" element={<RoleGuard allowedRoles={['admin', 'owner', 'proprietari', 'superadmin']}><BlogEditorPage /></RoleGuard>} />
          <Route path="users" element={<UserManagementPage />} />
          <Route path="portals" element={<PortalsPage />} />
          <Route path="portals/test" element={<PortalTestPage />} />
          <Route path="pages" element={<PageBuilderPage />} />
          <Route path="media" element={<MediaLibraryPage />} />
          <Route path="system/schema" element={<SystemSchemaPage />} />

          <Route path="communications">
            <Route path="dashboard" element={<MarketingDashboard />} />
            <Route path="emails" element={<SentEmailsPage />} />
            <Route path="templates" element={<TemplatesPage />} />
            <Route path="templates/new" element={<TemplateEditor />} />
            <Route path="templates/edit/:id" element={<TemplateEditor />} />
            <Route path="history" element={<CampaignHistoryPage />} />
          </Route>
        </Route>

        {/* Super Admin Global (dominio independiente) */}
        <Route path="/superadmin/login" element={<SuperAdminLoginPage />} />
        <Route path="/superadmin" element={<ProtectedRoute><RoleGuard allowedRoles={['superadmin']}><SuperAdminLayout /></RoleGuard></ProtectedRoute>}>
          <Route index element={<SuperAdminPage />} />
          <Route path="agencies" element={<SuperAdminPage />} />
          <Route path="users" element={<SuperAdminPage />} />
          <Route path="stats" element={<SuperAdminPage />} />
          <Route path="backups" element={<SuperAdminPage />} />
          <Route path="logs" element={<SuperAdminPage />} />
          <Route path="security" element={<SuperAdminPage />} />
          <Route path="system" element={<SuperAdminPage />} />
          <Route path="system/schema" element={<SystemSchemaPage />} />
        </Route>

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  )
}

function AppContent() {
  const { profile, user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const tenant = searchParams.get('tenant')
  
  const isSuperAdminPath = window.location.pathname.startsWith('/superadmin')

  const resolvedTenant = useMemo(() => {
    // If we are in superadmin, we don't want to load an agency theme necessarily, 
    // or at least we don't want to save/force it.
    if (isSuperAdminPath) return 'system'

    if (tenant) {
      localStorage.setItem('local_dev_tenant', tenant)
      return tenant
    }
    return localStorage.getItem('local_dev_tenant') || import.meta.env.VITE_AGENCY_SLUG || 'demo'
  }, [tenant, isSuperAdminPath])

  // Security Guard: Prevent tenant mismatch for non-superadmins
  useEffect(() => {
    // 1. Clear tenant from URL in SuperAdmin paths (Clean navigation)
    if (isSuperAdminPath && tenant) {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev)
        next.delete('tenant')
        return next
      }, { replace: true })
      return
    }

    // 2. Force tenant in URL if missing but exists in localStorage (Skip for SuperAdmin paths)
    if (!tenant && localStorage.getItem('local_dev_tenant') && !isSuperAdminPath) {
      const stored = localStorage.getItem('local_dev_tenant')
      if (stored) {
        setSearchParams(prev => {
          const next = new URLSearchParams(prev)
          next.set('tenant', stored)
          return next
        }, { replace: true })
      }
    }

    // 3. Prevent mismatch for logged users (Only in admin area)
    const isAdminPath = window.location.pathname.startsWith('/admin')
    if (user && profile && profile.role !== 'superadmin' && tenant && isAdminPath) {
      if (profile.slug && profile.slug !== tenant) {
        console.warn(`[Security] Tenant mismatch in admin area: User is ${profile.slug} but URL is ${tenant}. Redirecting...`)
        setSearchParams({ tenant: profile.slug })
      }
    }
  }, [user, profile, tenant, setSearchParams, isSuperAdminPath])

  return (
    <ThemeProvider agencySlug={resolvedTenant}>
      <SecurityShield />
      <AppRoutes />
    </ThemeProvider>
  )
}

export default function App() {
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      const overlays = document.querySelectorAll('.modal-overlay')
      if (overlays.length === 0) return
      const top = overlays[overlays.length - 1] as HTMLElement
      top.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }))
    }
    window.addEventListener('keydown', handleEsc)
    return () => window.removeEventListener('keydown', handleEsc)
  }, [])

  return (
    <BrowserRouter>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </BrowserRouter>
  )
}
